import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, FileSearch, Users, BarChart3, Clock, Lock } from 'lucide-react';
import InstallPWA from '../components/InstallPWA';
import { useSeo, SEO_KEYWORDS } from '../hooks/useSeo';

const LandingPage = () => {
  useSeo({ title: 'InvestiGate | Investigation & Case Management Software for Private Investigators', description: 'Investigation management software for private investigators, fraud examiners and researchers. Track cases, manage evidence with chain of custody, analyze documents with AI and generate PDF reports.', keywords: SEO_KEYWORDS });
  return (
    <div className="min-h-screen bg-gray-50">
      <InstallPWA />
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <Shield className="w-8 h-8 text-blue-600" />
            <span className="text-2xl font-bold text-gray-900">InvestiGate</span>
          </div>
          <div className="flex items-center space-x-4">
            <Link to="/pricing" className="text-gray-600 hover:text-gray-900">Pricing</Link>
            <Link to="/login" className="text-gray-600 hover:text-gray-900">Login</Link>
            <Link to="/register" className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition">
              Get Started
            </Link>
          </div>
        </div>
      </header>

      <section className="bg-gradient-to-br from-blue-900 to-blue-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl sm:text-6xl font-bold mb-6">Streamline Your Investigations</h1>
          <p className="text-xl sm:text-2xl mb-8 text-blue-100 max-w-3xl mx-auto">
            Investigation management software for private investigators, fraud examiners, researchers and law enforcement.
          </p>
          <div className="flex justify-center space-x-4">
            <Link to="/register" className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition">
              Start Free Trial
            </Link>
            <Link to="/pricing" className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-blue-800 transition">
              View Pricing
            </Link>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-12">Everything You Need to Investigate</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <FeatureCard icon={<FileSearch className="w-12 h-12 text-blue-600" />} title="Case Management" description="Organize and track private investigation, fraud and research cases in one secure platform." />
            <FeatureCard icon={<Shield className="w-12 h-12 text-blue-600" />} title="Evidence Collection" description="Securely upload, store and manage case evidence with a clear chain of custody." />
            <FeatureCard icon={<BarChart3 className="w-12 h-12 text-blue-600" />} title="AI-Powered Analysis" description="Use AI document analysis to extract key insights and build investigation reports." />
            <FeatureCard icon={<Users className="w-12 h-12 text-blue-600" />} title="Team Collaboration" description="Work seamlessly with your team and communicate in real-time." />
            <FeatureCard icon={<Clock className="w-12 h-12 text-blue-600" />} title="Progress Tracking" description="Monitor case timelines and track investigation milestones." />
            <FeatureCard icon={<Lock className="w-12 h-12 text-blue-600" />} title="Enterprise Security" description="Bank-level encryption and compliance with security standards." />
          </div>
        </div>
      </section>

      <section className="bg-blue-600 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-4">Ready to Get Started?</h2>
          <p className="text-xl mb-8 text-blue-100">Join investigators worldwide who trust InvestiGate.</p>
          <Link to="/register" className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition inline-block">
            Start Your Free Trial
          </Link>
        </div>
      </section>

      <footer className="bg-gray-900 text-gray-400 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="mb-4 md:mb-0">&copy; 2025 InvestiGate. All rights reserved.</p>
            <div className="flex space-x-6">
              <Link to="/terms" className="hover:text-white transition">Terms of Service</Link>
              <Link to="/privacy" className="hover:text-white transition">Privacy Policy</Link>
              <Link to="/pricing" className="hover:text-white transition">Pricing</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

const FeatureCard = ({ icon, title, description }) => (
  <div className="bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition">
    <div className="mb-4">{icon}</div>
    <h3 className="text-xl font-bold text-gray-900 mb-2">{title}</h3>
    <p className="text-gray-600">{description}</p>
  </div>
);

export default LandingPage;
